/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import fs from 'fs-extra';
import { bboxPolygon, area } from '@turf/turf';
import path from 'path';
import { isEmpty, isNil, forEach } from 'lodash';
import { check } from 'meteor/check';

import Annotations from '../../Annotations/Annotations';
import * as missionOps from '../../Missions/mission-operations';
import Missions from '../../Missions/Missions';
import Payloads from '../../Payloads/Payloads';
import * as projectOps from '../project-operations';
import Projects from '../Projects';
import RPAs from '../../RPAs/RPAs';
import * as videoOps from '../../Videos/video-operations';
import Videos from '../../Videos/Videos';

import checkAuthentication from '../../../modules/server/check-authentication';

import { deleteFolderRecursiveSync } from '../../../modules/server/file-system';
import { cleanObjectForExport } from '../../../modules/export-import-files';
import { returnBboxFromFeaturePoint } from '../../../modules/geojson-utilities';
import handleMethodException from '../../../modules/handle-method-exception';
import httpPromise from '../../../modules/http-call-promise-wrapper';

const DTM_AREA_LIMIT = 2 * 1e8; // square meters, 20 000 hectare

export const editProjectSchema = Projects.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'dtmFileInfo',
  'deleted',
  'shared',
  'done'
);
editProjectSchema.extend({ _id: String });

/**
 * Returns the project folder path given project id and user id, does not mean that the folder exists.
 *
 * @param {string} projectId - The project id.
 * @param {string} userId - The user id.
 * @returns {string} Returns the path for the project folder.
 */
const getProjectFolderPath = (projectId, userId) =>
  path.join(Meteor.settings.private.config.dataPath, 'users', userId, 'projects', projectId);

/**
 * Returns the path for the dtm file, does not mean that the file exists.
 *
 * @param {string} projectId - The project id.
 * @param {string} userId - The user id.
 * @returns {string} Returns the path for the dtm file.
 */
const getDTMFilePath = (projectId, userId) =>
  path.join(getProjectFolderPath(projectId, userId), `${projectId}.tif`);

/**
 * Checks if the DTM File Exists in the Server.
 *
 * @param {string} projectId - The project id.
 * @param {string} userId - The user id.
 * @returns {boolean} Return true if the file exists in the server, false otherwise.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const isDTMFileInServerAndValid = (projectId, userId) => {
  check(projectId, String);
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: projectId });
    const dtmPath = getDTMFilePath(projectId, userId);
    const doesFileExist = fs.existsSync(dtmPath);
    const isbboxValid = projectOps.isDTMbboxCorrect(project);

    return doesFileExist && isbboxValid;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts the project in the database.
 *
 * @async
 * @param {object} projectObject - The project object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new project.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (projectObject, userId) => {
  try {
    checkAuthentication();
    Projects.schema.validate(projectObject, {
      check,
      keys: ['name', 'description', 'mapLocation']
    });
    const newProjectID = Projects.insert({ owner: userId, ...projectObject });
    const newprojectDir = getProjectFolderPath(newProjectID, userId);
    await fs.ensureDir(newprojectDir);
    return newProjectID;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the project in the database.
 *
 * @param {object} projectObject - The project object.
 * @returns {string} Return the id of the updated project.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = projectObject => {
  try {
    checkAuthentication();
    editProjectSchema.validate(projectObject, { check });
    const projectId = projectOps.getId(projectObject);
    Projects.update(projectId, { $set: projectObject });
    return projectId;
    // Return _id so we can redirect to project after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the project in the database.
 *
 * @async
 * @param {string} userId - The user id that wants the project.
 * @param {string} projectId - The project id.
 * @returns {string} Return the id of the new project copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = async (userId, projectId) => {
  check(projectId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyProjectData = Projects.findOne({ _id: projectId });
    delete copyProjectData.owner;
    delete copyProjectData._id;
    delete copyProjectData.createdAt;
    delete copyProjectData.updatedAt;
    delete copyProjectData.shared;
    delete copyProjectData.deleted;
    delete copyProjectData.done;
    copyProjectData.name += ' - copy';
    const copyProjectId = Projects.insert({ owner: userId, ...copyProjectData });
    const copyProjectDir = getProjectFolderPath(copyProjectId, userId);
    await fs.ensureDir(copyProjectDir);
    return copyProjectId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the project in the database, if is GeomotionVideo it also sofdeleted the videos.
 *
 * @param {string} projectId - The project id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    Projects.update(projectId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the project shared or unshared.
 *
 * @param {string} projectId - The project id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleShared = (projectId, isShared) => {
  check(projectId, String);
  check(isShared, Boolean);
  try {
    checkAuthentication();
    Projects.update(projectId, { $set: { shared: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the project Location in the database.
 *
 * @param {string} projectId - The project id.
 * @param {object} projectLocation - The new value for the project location.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateProjectLocation = (projectId, projectLocation) => {
  check(projectId, String);
  check(projectLocation, Object);
  try {
    checkAuthentication();
    Projects.update(projectId, { $set: { mapLocation: projectLocation } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the project in the database, if is GeomotionVideo it also restores the videos in the project.
 *
 * @param {string} projectId - The project id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    Projects.update(projectId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the project and all it's content from database and file system.
 *
 * @param {string} projectId - The project id.
 * @param {string} userId - The user id.
 * @returns {object} Returns an object with number of files and folders deleted.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = (projectId, userId) => {
  check(projectId, String);
  try {
    checkAuthentication();
    const projectMissions = Missions.find({ project: projectId, owner: userId }).fetch();
    forEach(projectMissions, mission => {
      Missions.remove(missionOps.getId(mission));
    });
    const projectVideos = Videos.find({ project: projectId, owner: userId }).fetch();
    forEach(projectVideos, video => {
      Annotations.remove({
        owner: userId,
        'geoJSON.properties.video': videoOps.getId(video)
      });
      Videos.remove(videoOps.getId(video));
    });
    const projectDir = getProjectFolderPath(projectId, userId);
    Projects.remove(projectId);
    let deletedItems = '';
    try {
      deletedItems = deleteFolderRecursiveSync(projectDir);
    } catch (error) {
      console.error('No folder found');
    }
    return deletedItems;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the project completed or not.
 *
 * @param {string} projectId - The project id.
 * @param {boolean} isDone - The new value for the completed key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const setDone = (projectId, isDone) => {
  check(projectId, String);
  check(setDone, Boolean);
  try {
    checkAuthentication();
    Projects.update(projectId, { $set: { done: isDone } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Requests the server to produce a tif file for the bounding box supplied,
 * checking the bounding box to avoid overlapping and duplication.
 * FIXME: this can be optimized by only saving the tif files in a folder.
 *
 * @async
 * @param {object} projectObject - The project object.
 * Leaflet boundingbox 'southwest_lng,southwest_lat,northeast_lng,northeast_lat' format.
 * @returns {string} The string with the path to the remote file if there was no error.
 * @throws Error if something went wrong.
 */
export const requestTifFileCreation = async projectObject => {
  try {
    // Leaflet boundingbox 'southwest_lng,southwest_lat,northeast_lng,northeast_lat' format
    const projectBbox = projectOps.getBoundingBox(projectObject);
    if (isEmpty(projectBbox)) {
      throw new Error(`No Bounding Box For Project`);
    }
    const { requestTifURL, tifFileServerURL } = Meteor.settings.private;

    // Leaflet boundingbox 'southwest_lng,southwest_lat,northeast_lng,northeast_lat' format
    // The fetch nombre,long_min,lat_max,long_max,lat_min
    const bboxForFetch = [];
    [bboxForFetch[0], bboxForFetch[3], bboxForFetch[2], bboxForFetch[1]] = projectBbox;
    const options = {
      headers: { 'Content-Type': 'application/json' },
      data: [projectOps.getId(projectObject), ...bboxForFetch]
    };
    let produceTif;
    try {
      produceTif = await httpPromise('POST', requestTifURL, options);
    } catch (err) {
      throw new Error(`Server Error`);
    }
    if (produceTif.statusCode === 200) {
      const dtmFilePathRemote = `${tifFileServerURL}${projectOps.getId(projectObject)}.tif`;
      return dtmFilePathRemote;
    }
    throw new Error(`Request Failed Error`);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Downloads the tif file from the remote server,
 * checking the bounding box to avoid overlapping and duplication.
 * OVERWRITES the file if it already exists.
 * FIXME: this can be optimized by only saving the tif files in a folder.
 *
 * @param {string} remoteDTMPath - The path for the remote dtm file.
 * @param {string} destDTMPath - The path for the dtm file in our server.
 * @param {string} projectId - The project id.
 * @throws Error if something went wrong.
 */
export const downloadTifFileFromServer = async (remoteDTMPath, destDTMPath, projectId) => {
  try {
    let requestFile;
    const { deleteTifURL } = Meteor.settings.private;
    const options = {
      headers: { 'Content-Type': 'application/json' },
      data: { name: `${projectId}.tif` }
    };
    try {
      requestFile = await httpPromise('GET', remoteDTMPath, {
        npmRequestOptions: {
          encoding: null
        }
      });
    } catch (err) {
      // TODO: test for the error given if there is no file created
      throw new Error('Download Error');
    }
    // delete file if it exists
    if (fs.existsSync(destDTMPath)) {
      fs.unlinkSync(destDTMPath);
    }
    try {
      await fs.writeFile(destDTMPath, requestFile.content);
    } catch (error) {
      throw new Error('Error writting File');
    }
    try {
      await httpPromise('POST', deleteTifURL, options);
    } catch (error) {
      console.error(error);
    }
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Deletes the DTM file from server,
 * checking the bounding box to avoid overlapping and duplication.
 * FIXME: this can be optimized by only saving the tif files in a folder.
 *
 * @param {string} projectId - The project id.
 * @throws Error if something went wrong.
 */
export const deleteDTMFile = async projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: projectId });
    const dtmFilePath = getDTMFilePath(projectId, projectOps.getOwner(project));
    // delete file if it exists
    if (fs.existsSync(dtmFilePath)) {
      fs.unlinkSync(dtmFilePath);
      Projects.update(projectId, {
        $set: {
          dtmFileInfo: {
            doesFileExist: false,
            dtmBbox: []
          }
        }
      });
    }
  } catch (error) {
    handleMethodException(error);
  }
};
/**
 * Reads the DTM file from server.
 *
 * @param {string} projectId - The project id.
 * @returns {Buffer} Returns the file in a buffer array.
 * @throws Error if something went wrong.
 */
export const readDTMFile = async projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: projectId });
    const dtmFilePath = getDTMFilePath(projectId, projectOps.getOwner(project));
    // delete file if it exists
    if (fs.existsSync(dtmFilePath)) return fs.readFileSync(dtmFilePath);
    throw new Error('no file to read');
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the DTM file in tif format to get the elevation profile.
 * This file makes getting the elevation profile possible when
 * in an offline environment on the ground station.
 * It saves the file to the project folder with the name project.tif,
 * checking the bounding box to avoid overlapping and duplication.
 * OVERWRITES the file if it already exists.
 * FIXME: this can be optimized by only saving the tif files in a folder.
 *
 * @param {string} projectId - The project id.
 * @throws Error if something went wrong.
 */
export const getTifFileForProject = async projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: projectId });
    const dtmFilePath = getDTMFilePath(projectId, projectOps.getOwner(project));
    let projectBbox = projectOps.getBoundingBox(project);
    if (isEmpty(projectBbox)) {
      const projectLocation = projectOps.getMapLocation(project);
      projectBbox = returnBboxFromFeaturePoint(projectLocation);
      projectLocation.bbox = returnBboxFromFeaturePoint(projectLocation);
      updateProjectLocation(projectId, projectLocation);
    }
    const bboxArea = area(bboxPolygon(projectBbox));
    if (bboxArea > DTM_AREA_LIMIT) {
      throw new Error(`Area too big, choose a smaller area`);
    }
    const dtmFileInfo = {
      doesFileExist: true,
      dtmBbox: projectBbox
    };
    try {
      const remotePath = await requestTifFileCreation(project);
      await downloadTifFileFromServer(remotePath, dtmFilePath, projectId);
      Projects.update(projectId, { $set: { dtmFileInfo } });
    } catch (error) {
      console.error(error);
      throw error;
    }
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * @typedef {object} ProjectQuery
 * @property {number} totalCount The total number of projects not in the trash.
 * @property {number} deletedCount The total number of projects in the trash.
 * @property {number} completeCount The total number of projects completed.
 * @property {number} incompleteCount The total number of projects not completed.
 * @property {object[]} projects The projects not in the trash in the current page.
 * @property {object[]} deletedProjects The projects in the trash.
 */

/**
 * Fetchs all the data for the project to export.
 *
 * @param {string} projectId - The project id.
 * @returns {object} Returns an object that contains all project data.
 * @throws Error if something went wrong.
 */
export const exportProjectData = async projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: projectId });
    if (isNil(project)) {
      throw new Error('No Project Found');
    }
    if (Meteor.userId() !== projectOps.getOwner(project)) {
      throw new Error('You have no permission to do this');
    }
    // const videos = Videos.find({ project: projectId, deleted: { $eq: 'no' } }).fetch();
    // const annotations = Annotations.find({ project: projectId, deleted: { $eq: 'no' } }).fetch();
    const missions = Missions.find({
      project: projectId,
      deleted: { $eq: 'no' }
    }).fetch();
    const payloadIDsArray = [];
    const rpasIDsArray = [];
    forEach(missions, mission => {
      const payloadID = missionOps.getPayload(mission);
      const rpaID = missionOps.getRPA(mission);
      if (!payloadIDsArray.includes(payloadID)) {
        payloadIDsArray.push(payloadID);
      }
      if (!rpasIDsArray.includes(rpaID)) {
        rpasIDsArray.push(rpaID);
      }
    });
    const payloads = Payloads.find({ _id: { $in: payloadIDsArray } }).fetch();
    const rpas = RPAs.find({ _id: { $in: rpasIDsArray } }).fetch();
    const exportItem = {
      project,
      missions,
      payloads,
      rpas
      // videos,
      // annotations
    };
    cleanObjectForExport(exportItem);
    return exportItem;
  } catch (error) {
    handleMethodException(error);
  }
};
